import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Button, Grid, Card, CardContent, 
    TextField, MenuItem, Alert, CircularProgress, 
    Stepper, Step, StepLabel, List, ListItem, ListItemText,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { 
    Security as SecurityIcon, 
    VpnKey as KeyIcon, 
    VerifiedUser as VerifiedIcon,
    Groups as GroupsIcon,
    Lock as LockIcon,
    MeetingRoom as CeremonyIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';
import { designTokens } from '../../theme/designTokens';
import { 
    generateKeyPair, exportPublicKey,
    splitSecret, combineShares, encryptForUser, decryptForUser,
    encryptWithSymmetricKey, decryptWithSymmetricKey
} from '../../utils/cryptoUtil';
import { ListItemButton } from '@mui/material';

const OrganizationSecurity = () => {
    const { user, privateKey } = useAuth();
    const { showToast } = useToast();
    
    const [loading, setLoading] = useState(true);
    const [organization, setOrganization] = useState<any>(null);
    const [users, setUsers] = useState<any[]>([]);
    
    // Ceremony States
    const [activeStep, setActiveStep] = useState(0);
    const [selectedHolders, setSelectedHolders] = useState<string[]>([]);
    const [generatedMasterKey, setGeneratedMasterKey] = useState<any>(null);
    
    // Audit States
    const [auditShares, setAuditShares] = useState<string[]>([]);
    const [reconstitutedKey, setReconstitutedKey] = useState<string | null>(null);
    const [collectingShare, setCollectingShare] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [orgRes, usersRes] = await Promise.all([
                api.get('/api/organizations/my/organization'),
                api.get('/api/users')
            ]);
            setOrganization(orgRes.data);
            // Filter users by role
            setUsers(usersRes.data.filter((u: any) => ['of-admin', 'super-admin'].includes(u.role)));
        } catch (err) {
            console.error(err);
            showToast('Erreur de chargement', 'error');
        } finally {
            setLoading(false);
        }
    };

    const startCeremony = async () => {
        try {
            setLoading(true);
            // 1. Generate RSA MasterKey
            const keyPair = await generateKeyPair();
            
            // 2. Prepare the Private Key for storage (Envelope Encryption)
            const exportedPrivate = await window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey);
            
            // 3. Generate a random 32-byte secret for SSS (This becomes our Master-DEK)
            const masterDEK = window.crypto.getRandomValues(new Uint8Array(32));
            
            // 4. Encrypt the RSA Private Key with the Master-DEK
            const encryptedPrivateStr = await encryptWithSymmetricKey(new Uint8Array(exportedPrivate), masterDEK);

            // 5. Split the 32-byte Master-DEK using SSS (3 shares, threshold 3)
            const shares = splitSecret(masterDEK, 3, 3);
            
            // 6. Encrypt DEK shares for selected holders
            const encryptedShares = await Promise.all(selectedHolders.map(async (uid, index) => {
                const holder = users.find(u => u._id === uid);
                if (!holder?.publicKey) throw new Error(`Clé publique manquante pour ${holder?.firstName}`);
                
                const encrypted = await encryptForUser(shares[index], holder.publicKey);
                return { userId: uid, encryptedShare: encrypted };
            }));

            const pubKeyStr = await exportPublicKey(keyPair.publicKey);

            // 7. Save to Organization
            await api.put('/api/organizations/my/organization/master-key', {
                masterPublicKey: pubKeyStr,
                masterEncryptedPrivateKey: encryptedPrivateStr,
                masterKeyShares: encryptedShares
            });

            showToast('Cérémonie terminée : La MasterKey est activée.', 'success');
            fetchData();
        } catch (err: any) {
            console.error(err);
            showToast(err.message || 'Erreur lors de la cérémonie', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleHolderToggle = (uid: string) => {
        const target = users.find(u => u._id === uid);
        if (!target) return;

        setSelectedHolders(prev => {
            if (prev.includes(uid)) {
                return prev.filter(id => id !== uid);
            }

            // Enforce logic: 1 OF-Admin, 2 Super-Admins
            const selected = prev.map(id => users.find(u => u._id === id));
            const selectedOfAdmins = selected.filter(s => s?.role === 'of-admin');
            const selectedSuperAdmins = selected.filter(s => s?.role === 'super-admin');

            if (target.role === 'of-admin') {
                if (selectedOfAdmins.length >= 1) {
                    showToast("Un seul responsable OF peut être sélectionné", "warning");
                    return prev;
                }
            } else if (target.role === 'super-admin') {
                if (selectedSuperAdmins.length >= 2) {
                    showToast("Maximum deux super-admins peuvent être sélectionnés", "warning");
                    return prev;
                }
            }
            return [...prev, uid];
        });
    };

    const isConfigurationValid = () => {
        const selected = selectedHolders.map(id => users.find(u => u._id === id));
        return (
            selected.filter(s => s?.role === 'of-admin').length === 1 &&
            selected.filter(s => s?.role === 'super-admin').length === 2
        );
    };

    const triggerAudit = async () => {
        if (!privateKey) {
            showToast('Votre clé privée doit être déverrouillée (onglet Messagerie)', 'warning');
            return;
        }
        
        const myShare = organization.masterKeyShares.find((s: any) => s.userId === user?._id);
        if (!myShare) {
            showToast("Vous ne possédez pas de fragment de cette MasterKey", "error");
            return;
        }

        try {
            const decryptedShare = await decryptForUser(myShare.encryptedShare, privateKey);
            const newShares = [...auditShares, decryptedShare];
            setAuditShares(newShares);
            
            if (newShares.length === 3) {
                // RECONSTITUTE Master-DEK
                const masterDEK = combineShares(newShares);
                
                // DECRYPT Master Private Key
                const masterPrivateBytes = await decryptWithSymmetricKey(organization.masterEncryptedPrivateKey, masterDEK);
                
                // Import the Private Key
                const masterPrivateKey = await window.crypto.subtle.importKey(
                    'pkcs8',
                    masterPrivateBytes.buffer as any,
                    { name: 'RSA-OAEP', hash: 'SHA-256' } as any,
                    false,
                    ['decrypt']
                );

                setReconstitutedKey("MASTER_KEY_LOADED");
                showToast("Audit réussi : MasterKey reconstituée", "success");
                console.log("MasterKey temporary session key:", masterPrivateKey);
            } else {
                setCollectingShare(true);
            }
        } catch (err) {
            console.error(err);
            showToast("Erreur lors de l'audit ou déchiffrement du fragment", "error");
        }
    };

    if (loading) return <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} />;

    return (
        <Box sx={{ p: 4, maxWidth: 900, mx: 'auto' }}>
            <Box sx={designTokens.pageHeader}>
                <Typography variant="h4" sx={designTokens.pageTitle}>
                    <SecurityIcon fontSize="large" sx={{ mr: 2 }} /> Sécurité & MasterKey OF
                </Typography>
            </Box>

            {!organization?.isMasterKeySet ? (
                <Card sx={designTokens.glassCard}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom color="primary">
                            <CeremonyIcon sx={{ verticalAlign: 'middle', mr: 1 }} /> Cérémonie de remise des clés
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary' }}>
                            La MasterKey permet la récupération des données de messagerie en cas de perte de clé d'un utilisateur 
                            ou pour des raisons d'audit légal. Elle est fragmentée en 3 morceaux : les 3 sont requis pour l'utiliser.
                        </Typography>

                        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                            <Step><StepLabel>Sélection des Gardiens</StepLabel></Step>
                            <Step><StepLabel>Génération & Fragmentation</StepLabel></Step>
                            <Step><StepLabel>Finalisation</StepLabel></Step>
                        </Stepper>

                        {activeStep === 0 && (
                            <Box>
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="overline" color="primary" sx={{ fontWeight: 'bold' }}>
                                        Responsable OF (1 requis)
                                    </Typography>
                                    <List sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1, mb: 2 }}>
                                        {users.filter(u => u.role === 'of-admin').map(u => (
                                            <ListItem key={u._id} disablePadding secondaryAction={selectedHolders.includes(u._id) && <VerifiedIcon color="success" />}>
                                                <ListItemButton onClick={() => handleHolderToggle(u._id)} selected={selectedHolders.includes(u._id)} disabled={!u.publicKey}>
                                                    <ListItemText primary={`${u.firstName} ${u.lastName}`} secondary={u.publicKey ? "Gérant OF" : "⚠️ Pas de clé publique"} />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>

                                    <Typography variant="overline" color="primary" sx={{ fontWeight: 'bold' }}>
                                        Super-Administrateurs (2 requis)
                                    </Typography>
                                    <List sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
                                        {users.filter(u => u.role === 'super-admin').map(u => (
                                            <ListItem key={u._id} disablePadding secondaryAction={selectedHolders.includes(u._id) && <VerifiedIcon color="success" />}>
                                                <ListItemButton onClick={() => handleHolderToggle(u._id)} selected={selectedHolders.includes(u._id)} disabled={!u.publicKey}>
                                                    <ListItemText primary={`${u.firstName} ${u.lastName}`} secondary={u.publicKey ? "Super Admin" : "⚠️ Pas de clé publique"} />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                                <Button 
                                    variant="contained" 
                                    sx={{ mt: 3, ...designTokens.actionButton }}
                                    disabled={!isConfigurationValid()}
                                    onClick={() => setActiveStep(1)}
                                >
                                    Suivant ({selectedHolders.length}/3)
                                </Button>
                            </Box>
                        )}

                        {activeStep === 1 && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="h6" gutterBottom>Confirmation de la Fragmentation</Typography>
                                <Alert severity="info" sx={{ mb: 3 }}>
                                    En cliquant sur "Générer", une paire de clés RSA sera créée. La clé privée sera divisée en 3 fragments (SSS 3/3) 
                                    chiffrés spécifiquement pour les gardiens choisis.
                                </Alert>
                                <Button variant="contained" onClick={startCeremony} sx={designTokens.actionButton}>
                                    Générer et Distribuer
                                </Button>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={4}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={designTokens.glassCard}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                    <VerifiedIcon color="success" sx={{ mr: 1 }} /> MasterKey Active
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    La MasterKey de l'organisation est configurée et utilisée pour le chiffrement de tous les nouveaux messages.
                                </Typography>
                                <Typography variant="subtitle2" gutterBottom>Gardiens enregistrés :</Typography>
                                <List>
                                    {organization.masterKeyShares.map((s: any) => {
                                        const h = users.find(u => u._id === s.userId);
                                        return (
                                            <ListItem key={s.userId}>
                                                <ListItemText primary={h ? `${h.firstName} ${h.lastName}` : s.userId} />
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ ...designTokens.glassCard, border: '1px solid rgba(255, 152, 0, 0.3)' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom color="warning.main">
                                    <LockIcon sx={{ verticalAlign: 'middle', mr: 1 }} /> Audit & Récupération
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Pour reconstituer la MasterKey, connectez-vous avec les 3 gardiens sur ce poste.
                                </Typography>
                                
                                {reconstitutedKey ? (
                                    <Alert severity="success">MasterKey reconstituée et active pour cette session d'audit.</Alert>
                                ) : (
                                    <Button 
                                        variant="outlined" 
                                        color="warning" 
                                        fullWidth 
                                        onClick={triggerAudit}
                                    >
                                        Initier un Audit ({auditShares.length}/3 fragments)
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}
            
            {/* Simple Audit Modal to collect other shares (simulated for now, would be better with a proper session storage or real-time) */}
            <Dialog open={collectingShare} onClose={() => setCollectingShare(false)}>
                <DialogTitle>Collecte de fragments d'audit</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Dans une implémentation réelle, les autres gardiens scannent un QR code ou reçoivent une notification 
                        pour valider l'audit. Pour cette démo, le quorum est simulé une fois que le premier gardien initie.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCollectingShare(false)}>Annuler</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OrganizationSecurity;
